import { Auth, CognitoUser } from '@aws-amplify/auth';
import { DateUtils } from '@aws-amplify/core';
import { navigate } from 'hookrouter';
import { toast } from 'react-toastify';
import {
  CognitoUserPool,
  CognitoUser as AmazonCognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { change, SubmissionError } from 'redux-form';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { CustomTheme } from '@idearoom/ir-code/lib';
import { config as configuration } from '../config/config';
import { AppRoutes } from '../constants/AppRoutes';
import { AuthStatus } from '../constants/AuthStatus';
import { SignInChallenges } from '../constants/Cognito';
import { Dialogs } from '../constants/Dialogs';
import {
  ForgotPasswordFormFields,
  ImpersonationDialogFormFields,
  PasswordFormFields,
  PreferencesFormFields,
  ProfileFormFields,
  SignInFormFields,
  SignUpFormFields,
} from '../constants/FormFields';
import { Forms } from '../constants/Forms';
import { LocalStorage } from '../constants/LocalStorage';
import { MenuItems } from '../constants/MenuItems';
import { SystemGroups } from '../constants/SystemGroups';
import {
  clearErrors,
  CurrentUserActionTypes,
  impersonationFailed,
  impersonationSuccess,
  setCurrentUserAuthProvider,
  setCurrentUserAuthStatus,
  setCurrentUserData,
  setErrors,
  setGroupConfig,
  signedIn,
  signedInSuccess,
  signOut as signOutAction,
  updateCurrentUser,
  signedInFailed,
  setUserPreferences,
  fetchUserPreferences as fetchUserPreferencesFunc,
  fetchUserPreferencesComplete,
  saveUserPreferences as saveUserPreferencesFunc,
  saveUserPreferencesComplete,
  setSigningIn,
} from '../ducks/currentUser';
import { openDialog, closeDialog } from '../ducks/dialogSlice';
import { setForgotPasswordState } from '../ducks/forgotPassword';
import { setFocusedMenuItem } from '../ducks/menu';
import { openSalesViewWhatsNewSuccess } from '../ducks/settings';
import { setSignUpState } from '../ducks/signUp';
import {
  openTour,
  setTourSteps,
  getTheme as getThemeFunc,
  setSelectedClientId,
  setTheme,
  setCurrency,
  setDefaultClientId,
  ViewerActionTypes,
  getVisibleLanguages,
  setUseMetricUnits,
} from '../ducks/viewer';
import { portalService } from '../services/portalService';
import { AppState } from '../types/AppState';
import { Configurator, ConfiguratorEnabledOnProps } from '../types/Configurator';
import { Dealer } from '../types/Dealer';
import { Group } from '../types/Group';
import { WhatsNew } from '../types/WhatsNew';
import { unknownWhatsNew } from '../constants/WhatsNew';
import { mapConfiguratorToClientId } from '../utils/clientIdUtils';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';
import { getTourSteps } from '../utils/viewerUtils';
import { getEnabledOnProperty, extractThemeFromConfig } from '../utils/vendorDataUtils';
import { CurrentUserState } from '../types/CurrentUserState';
import { resetSearch } from '../ducks/search';
import { setQueryParam } from '../utils/urlUtils';
import { CurrentUserData } from '../types/User';
import { SignUpState } from '../types/SignUpState';
import { SALESVIEW } from '../constants/App';
import { GroupMember } from '../types/GroupMember';
import { SessionStorage } from '../constants/SessionStorage';
import { i18n } from '../i18n';
import { I18nKeys } from '../constants/I18nKeys';
import { referenceConfigurator } from '../constants/Configurator';
import { fetchConfigVendor } from '../utils/configuratorUtils';
import { extractErrorProps } from '../utils/errorUtils';
import { getGroupIdFromUrl } from '../utils/clientDataUtils';
import { defaultErrorMessage } from '../constants/Error';
import { UserPreference } from '../constants/User';
import { EventName, userEventAction } from '../analytics/AnalyticsTypes';

/* global localStorage */

function* fetchCurrentUserData(): Generator {
  const availableDealers: Dealer[] = [];
  try {
    // API gateway will only permit requests if the clock skew is less than 5 minutes.
    // Once logged in adjust for any offset in the users clock so that API requests do
    // not fail. The symptoms of a failed request is a CORS InvalidSignatureException.
    yield Auth.currentAuthenticatedUser().then((u) => {
      DateUtils.setClockOffset(-u.signInUserSession.clockDrift * 1000);
    });

    yield put(setCurrentUserAuthStatus(AuthStatus.SignedIn));

    const { groupIds } = (yield portalService.getCurrentUsersGroups()) as { groupIds: string[] };

    // If user is not in any groups, throw an error
    if (!groupIds) {
      throw new Error('User not associated with any groups.');
    }

    // Check that the user is a member of one of the groups or in the IdeaRoom group, if so,
    // use that group.
    const groupIdIsValid = (groupId: string) =>
      groupIds.some((group: string) => group === groupId) ||
      groupIds.some((group: string) => group === SystemGroups.IdeaRoom);

    // Get the group from the uri path. /portal/data/:groupId/...
    const pathGroupId = window.location.pathname.startsWith(AppRoutes.ClientData) ? getGroupIdFromUrl() : undefined;
    // Get the previously selected group from local storage.
    const previouslySelectedGroupId = localStorage.getItem(LocalStorage.SelectedGroupId);

    let selectedGroupId: string;
    if (pathGroupId && groupIdIsValid(pathGroupId)) {
      selectedGroupId = pathGroupId;
    } else if (previouslySelectedGroupId && groupIdIsValid(previouslySelectedGroupId)) {
      selectedGroupId = previouslySelectedGroupId;
    } else {
      // If the user is not a member of the group, default to the first group
      // in their list of groups. If the user is in the IdeaRoom group, default to it.
      selectedGroupId = groupIds.some((group: string) => group === SystemGroups.IdeaRoom)
        ? SystemGroups.IdeaRoom
        : groupIds[0];
    }
    localStorage.setItem(LocalStorage.SelectedGroupId, selectedGroupId);

    // Get the stored whatsNewKey, default to 0, then get the latest 3 What's New records
    // Check to see if there are any that have been unseen based on the whatsNewKey
    // If so, update the stored whatsNewKey and open the dialog with the unseen What's New
    const whatsNewKey = parseInt(localStorage.getItem(LocalStorage.WhatsNewKey) || '0', 10) || 0;
    const { whatsNew: salesviewWhatsNew = [unknownWhatsNew] } = (yield portalService.getLatestWhatsNew(
      SALESVIEW,
      3,
    )) as {
      whatsNew: WhatsNew[];
    };
    const { whatsNew: clientSalesviewWhatsNew = [unknownWhatsNew] } = (yield portalService.getLatestWhatsNew(
      `${SALESVIEW}-${selectedGroupId}`,
      3,
    )) as {
      whatsNew: WhatsNew[];
    };
    const whatsNew = [...salesviewWhatsNew, ...clientSalesviewWhatsNew];
    const largestId = Math.max(...whatsNew.map((wn) => wn.id));
    const unseenWhatsNew = largestId >= whatsNewKey ? whatsNew.filter((what) => what.id > whatsNewKey) : [];
    if (unseenWhatsNew.length) {
      localStorage.setItem(LocalStorage.WhatsNewKey, largestId.toString());
      yield put(openSalesViewWhatsNewSuccess(unseenWhatsNew));
      yield put(openDialog({ dialog: Dialogs.WhatsNewSalesView }));
    }

    const tourIndex = localStorage.getItem(LocalStorage.TourIndex) || undefined;
    if (tourIndex === undefined) {
      yield put(setTourSteps(getTourSteps(groupIds)));
      yield put(openTour());
    }

    let user;
    let group;

    try {
      ({ user, group } = (yield portalService.getCurrentUserData(selectedGroupId)) as CurrentUserData);
    } catch (e) {
      localStorage.removeItem(LocalStorage.SelectedGroupId);
      throw e;
    }

    yield put(fetchUserPreferencesFunc());

    const currentPath = window.location.pathname;
    if (!currentPath || !currentPath.startsWith(AppRoutes.Portal)) {
      if (selectedGroupId === SystemGroups.IdeaRoom) {
        yield put(setFocusedMenuItem(MenuItems.Groups));
        navigate(AppRoutes.Groups);
      } else {
        yield put(setFocusedMenuItem(MenuItems.Leads));
        navigate(AppRoutes.Leads);

        // Set a uuid query param if it exists in session storage
        setQueryParam('uuid');
      }
    } else {
      // Selects the menu item based on the current URL path
      let focusedMenuItem;
      if (currentPath.startsWith(AppRoutes.Groups) || currentPath.replace(/\/$/, '') === AppRoutes.Portal) {
        if (selectedGroupId === SystemGroups.IdeaRoom) {
          focusedMenuItem = MenuItems.Groups;
        } else {
          yield put(setFocusedMenuItem(MenuItems.Leads));
          navigate(AppRoutes.Leads);
        }
      } else if (currentPath.startsWith(AppRoutes.Leads)) {
        if (selectedGroupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Leads;
        }
      } else if (currentPath.startsWith(AppRoutes.Pricing)) {
        if (selectedGroupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Pricing;
        }
      } else if (currentPath.startsWith(AppRoutes.Users)) {
        if (selectedGroupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Users;
        }
      } else if (currentPath.startsWith(AppRoutes.Dealers)) {
        if (selectedGroupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Dealers;
        }
      } else if (currentPath.startsWith(AppRoutes.Sites)) {
        if (selectedGroupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Sites;
        }
      }
      if (focusedMenuItem !== undefined) {
        yield put(setFocusedMenuItem(focusedMenuItem));
      }
    }

    if (group) {
      let { configurators = [] } = group;
      if (configurators && configurators.length > 0) {
        const [firstConfig] = configurators;
        const updatedConfig = (yield call(fetchConfigVendor, firstConfig)) as Configurator;
        const defaultClientId = mapConfiguratorToClientId(updatedConfig);

        yield put(setSelectedClientId(defaultClientId));
        yield put(setDefaultClientId(defaultClientId));

        // Don't race theme setting after setting clientId
        yield take(ViewerActionTypes.GetThemeComplete);
        const vendorTheme = extractThemeFromConfig(updatedConfig);
        const { vendorData: { vendor: { currency, useMetricUnits, languages = 'en' } = {} } = {} } = updatedConfig as {
          vendorData: { vendor: { currency: string; useMetricUnits?: boolean; languages: string } };
        };
        yield put(setTheme(vendorTheme));
        yield put(setCurrency(currency));
        yield put(setUseMetricUnits(useMetricUnits));
        yield put(getVisibleLanguages(languages.split(',')));
      }
      configurators = (yield all(
        (configurators as Configurator[]).map(async (config) => fetchConfigVendor(config)),
      )) as Configurator[];

      // Sort them alphabetically
      configurators.sort((a: Configurator, b: Configurator) => ((a.name || '') > (b.name || '') ? 1 : -1));
      const clientIds = configurators.map((configurator: Configurator) => mapConfiguratorToClientId(configurator));
      const { dealers } = (yield portalService.getAllDealers(clientIds, group.groupId)) as { dealers: Dealer[] };
      availableDealers.push(...dealers.filter((dealer) => dealer));

      if (group.groupId === 'IdeaRoom') {
        configurators.push(referenceConfigurator);
        yield put(setSelectedClientId(referenceConfigurator.clientId));
        yield put(setDefaultClientId(referenceConfigurator.clientId));
      }

      group = { ...group, configurators };
    }
    const { groups } = (yield portalService.getGroups()) as { groups: Group[] };

    yield put(setCurrentUserData(user, group, groups, availableDealers));
    yield put(signedInSuccess(user.username, group.groupId));
  } catch (error) {
    const { message } = error as Error;
    yield call(toast.error, message);

    yield put(signedInFailed());
    yield put(signOutAction());
  }
}

function* changeGroup({ payload: { groupId } }: any): Generator {
  try {
    const availableDealers: Dealer[] = [];

    localStorage.setItem(LocalStorage.SelectedGroupId, groupId);

    let user;
    let group;

    try {
      ({ user, group } = (yield portalService.getCurrentUserData(groupId)) as CurrentUserData);
    } catch (e) {
      localStorage.removeItem(LocalStorage.SelectedGroupId);
      throw e;
    }

    if (group) {
      // If on the client data page, update the GroupId in the URL
      if (window.location.pathname.startsWith(AppRoutes.ClientData)) {
        navigate(`${AppRoutes.ClientData}/${groupId}`);
      }

      let { configurators = [] } = group;
      if (configurators && configurators.length > 0) {
        const [firstConfig] = configurators;
        const updatedConfig = (yield call(fetchConfigVendor, firstConfig)) as Configurator;
        const defaultClientId = mapConfiguratorToClientId(updatedConfig);

        yield put(setSelectedClientId(defaultClientId));
        yield put(setDefaultClientId(defaultClientId));

        // Don't race theme setting after setting clientId
        yield take(ViewerActionTypes.GetThemeComplete);
        const vendorTheme = extractThemeFromConfig(updatedConfig);
        const { vendorData: { vendor: { currency, useMetricUnits, languages = 'en' } = {} } = {} } = updatedConfig as {
          vendorData: { vendor: { currency: string; useMetricUnits?: boolean; languages: string } };
        };
        yield put(setTheme(vendorTheme));
        yield put(setCurrency(currency));
        yield put(setUseMetricUnits(useMetricUnits));
        yield put(getVisibleLanguages(languages.split(',')));
      }
      configurators = (yield all(
        (configurators as Configurator[]).map(async (config) => fetchConfigVendor(config)),
      )) as Configurator[];

      // Sort them alphabetically
      configurators.sort((a: Configurator, b: Configurator) => ((a.name || '') > (b.name || '') ? 1 : -1));
      const clientIds = configurators.map((configurator: Configurator) => mapConfiguratorToClientId(configurator));
      const { dealers } = (yield portalService.getAllDealers(clientIds, group.groupId)) as { dealers: Dealer[] };
      availableDealers.push(...dealers.filter((dealer) => dealer));

      if (group.groupId === 'IdeaRoom') {
        configurators.push(referenceConfigurator);
        yield put(setSelectedClientId(referenceConfigurator.clientId));
        yield put(setDefaultClientId(referenceConfigurator.clientId));
      }

      group = { ...group, configurators };
    }
    const { groups } = (yield portalService.getGroups()) as { groups: Group[] };

    yield put(resetSearch());

    const currentPath = window.location.pathname;
    if (!currentPath || !currentPath.startsWith(AppRoutes.Portal)) {
      if (groupId === SystemGroups.IdeaRoom) {
        yield put(setFocusedMenuItem(MenuItems.Groups));
        navigate(AppRoutes.Groups);
      } else {
        yield put(setFocusedMenuItem(MenuItems.Leads));
        navigate(AppRoutes.Leads);
      }
    } else {
      // Selects the menu item based on the current URL path
      let focusedMenuItem;
      if (currentPath.startsWith(AppRoutes.Groups)) {
        if (groupId === SystemGroups.IdeaRoom) {
          focusedMenuItem = MenuItems.Groups;
        } else {
          yield put(setFocusedMenuItem(MenuItems.Leads));
          navigate(AppRoutes.Leads);
        }
      } else if (currentPath.startsWith(AppRoutes.Leads)) {
        if (groupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Leads;
        }
      } else if (currentPath.startsWith(AppRoutes.Pricing)) {
        if (groupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else if (
          group.configurators.some((config: Configurator) =>
            getEnabledOnProperty(config.vendorData, ConfiguratorEnabledOnProps.PricingEnabled, true),
          )
        ) {
          focusedMenuItem = MenuItems.Pricing;
        } else {
          yield put(setFocusedMenuItem(MenuItems.Leads));
          navigate(AppRoutes.Leads);
        }
      } else if (currentPath.startsWith(AppRoutes.Users)) {
        if (groupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Users;
        }
      } else if (currentPath.startsWith(AppRoutes.Dealers)) {
        if (groupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Dealers;
        }
      } else if (currentPath.startsWith(AppRoutes.Sites)) {
        if (groupId === SystemGroups.IdeaRoom) {
          yield put(setFocusedMenuItem(MenuItems.Groups));
          navigate(AppRoutes.Groups);
        } else {
          focusedMenuItem = MenuItems.Sites;
        }
      }
      if (focusedMenuItem !== undefined) {
        yield put(setFocusedMenuItem(focusedMenuItem));
      }
    }

    yield put(setCurrentUserData(user, group, groups, availableDealers));
  } catch (error) {
    const { message } = error as Error;
    yield call(toast.error, message);
  }
}

function* submitSignIn({
  reject,
  resolve,
  values: { [SignInFormFields.Email]: email, [SignInFormFields.Password]: password },
}: any): Generator {
  const isSigningIn = yield select(({ currentUser: { signingIn } }: AppState) => signingIn);
  if (isSigningIn) {
    yield call(resolve);
    return;
  }

  yield put(setSigningIn(true));

  const trimmedEmail = email.trim();
  try {
    // Remove this after upgrading to aws-amplify
    interface tempCognitoUser extends CognitoUser {
      challengeName: any;
      challengeParam: any;
    }
    const cognitoUser = (yield Auth.signIn(trimmedEmail, password).finally(() =>
      put(setSigningIn(false)),
    )) as tempCognitoUser;

    yield put(setSigningIn(false));

    const { challengeName, challengeParam } = cognitoUser;
    if (challengeName === SignInChallenges.NewPasswordRequired) {
      yield call(resolve);

      const { name: firstName, given_name: lastName } = challengeParam.userAttributes;
      yield put(setSignUpState({ email: trimmedEmail, firstName, lastName, authData: cognitoUser }));

      // User needs to sign up before continuing
      yield navigate(AppRoutes.SignUp);
    } else if (challengeName === undefined) {
      // User successfully signed in
      yield put(signedIn());

      yield call(resolve);
    }
  } catch (error) {
    yield put(setSigningIn(false));
    const { code = '' } = error as { code?: string };
    if (code === 'PasswordResetRequiredException') {
      yield put(setCurrentUserAuthStatus(AuthStatus.ForgotPasswordCodeSent));

      yield navigate(AppRoutes.ForgotPassword);

      yield put(change(Forms.ForgotPassword, ForgotPasswordFormFields.Email, trimmedEmail));
    } else if (['NotAuthorizedException', 'UserNotFoundException'].includes(code)) {
      yield call(
        reject,
        new SubmissionError({
          _error: "The email address or password doesn't match. Please try again.",
        }),
      );
    } else {
      const { errorMessage } = extractErrorProps(error);
      yield call(reject, new SubmissionError({ _error: errorMessage }));
    }
  }
}

function* submitSignUp({
  reject,
  resolve,
  values: {
    [SignUpFormFields.FirstName]: firstName,
    [SignUpFormFields.LastName]: lastName,
    [SignUpFormFields.NewPassword]: newPassword,
  },
}: any): Generator {
  try {
    const { authData, email } = (yield select(({ signUp }: AppState) => signUp)) as SignUpState;

    yield Auth.completeNewPassword(authData, newPassword, {
      email,
      given_name: lastName,
      name: firstName,
    });

    // Clear sign up state
    yield put(setSignUpState({ email: undefined, authData: undefined }));

    // User successfully signed in
    yield put(signedIn());

    yield call(resolve);
  } catch (error) {
    const { message } = error as Error;
    yield call(reject, new SubmissionError({ _error: message }));
  }
}

const clearUserLocalStorageItems = () => {
  [
    LocalStorage.LastPricingBaseMergeCommit,
    LocalStorage.LastClientUpdateMergeCommit,
    LocalStorage.HaveShownPricingBaseDeletingPriceDialog,
  ].forEach((key) => localStorage.removeItem(key));
};

function* signOut(): Generator {
  try {
    yield Auth.signOut();
    clearUserLocalStorageItems();

    const currentPath = window.location.pathname;
    if (!currentPath.includes(AppRoutes.Sso)) {
      navigate(AppRoutes.SignIn);
    }
  } catch (error) {
    const { message } = error as Error;
    yield call(toast.error, message);
  }
}

function* sendForgotPasswordCode({
  reject,
  resolve,
  values: {
    [ForgotPasswordFormFields.Email]: email,
    [ForgotPasswordFormFields.NewPassword]: newPassword,
    [ForgotPasswordFormFields.VerificationCode]: verificationCode,
  },
}: any): Generator {
  try {
    const authenticationStatus = yield select(({ currentUser: { authStatus } }: AppState) => authStatus);

    if (authenticationStatus === AuthStatus.ForgotPasswordCodeSent) {
      // Remove any whitespace from the verification code. AWS regex for verification code ([\S]+) doesn't allow whitespace.
      yield Auth.forgotPasswordSubmit(email, verificationCode.replace(/\s+/g, ''), newPassword);

      yield put(setCurrentUserAuthStatus(AuthStatus.PasswordReset));

      yield call(resolve);
    } else {
      yield Auth.forgotPassword(email);

      yield put(setForgotPasswordState({ email }));
      yield put(setCurrentUserAuthStatus(AuthStatus.ForgotPasswordCodeSent));

      yield call(resolve);
    }
  } catch (error) {
    const { errorMessage } = extractErrorProps(error);
    yield call(reject, new SubmissionError({ _error: errorMessage }));
  }
}

function* submitProfileForm({
  reject,
  resolve,
  values: {
    [ProfileFormFields.FirstName]: firstName,
    [ProfileFormFields.LastName]: lastName,
    [ProfileFormFields.PhoneNumber]: phone,
  },
}: any): Generator {
  try {
    const cognitoUser = yield Auth.currentAuthenticatedUser();

    const formattedPhoneNumber = formatPhoneNumber(phone);
    yield Auth.updateUserAttributes(cognitoUser, {
      given_name: lastName,
      name: firstName,
      'custom:phone': formattedPhoneNumber,
    });

    const { group } = (yield select(({ currentUser }: AppState) => currentUser)) as { group: Group };
    const { user } = (yield portalService.getCurrentUserData(group.groupId, true)) as CurrentUserData;
    yield put(updateCurrentUser(user));

    yield call(resolve);

    yield call(toast.success, `Profile successfully updated.`);
  } catch (error) {
    const { errorMessage } = extractErrorProps(error);
    yield call(reject, new SubmissionError({ _error: errorMessage }));
  }
}

function* submitPreferencesForm({ reject, resolve, values }: any): Generator {
  try {
    const { [PreferencesFormFields.Theme]: theme, [PreferencesFormFields.MiniMap]: miniMap } = values;
    if (!['light', 'vs-dark', ...Object.values(CustomTheme)].includes(theme)) {
      throw new Error('Invalid theme');
    }

    yield put(
      saveUserPreferencesFunc(UserPreference.ProfilePreferences, {
        [PreferencesFormFields.Theme]: theme,
        [PreferencesFormFields.MiniMap]: miniMap,
      }),
    );

    yield take(CurrentUserActionTypes.SAVE_USER_PREFERENCES_COMPLETE);

    const { preferences: { [UserPreference.ProfilePreferences]: profilePreferences = {} } = {} } = (yield select(
      ({ currentUser }: AppState) => currentUser,
    )) as CurrentUserState;

    if (!Object.entries(values).every(([key, value]) => (profilePreferences as any)[key] === value)) {
      throw new Error('Preferences failed to update.');
    }

    yield call(resolve);

    yield call(toast.success, `Preferences successfully updated.`);
  } catch (error) {
    const { errorMessage } = extractErrorProps(error);
    yield call(reject, new SubmissionError({ _error: errorMessage }));
  }
}

function* submitPasswordForm({
  reject,
  resolve,
  values: { [PasswordFormFields.CurrentPassword]: currentPassword, [PasswordFormFields.NewPassword]: newPassword },
}: any): Generator {
  try {
    const cognitoUser = yield Auth.currentAuthenticatedUser();

    yield Auth.changePassword(cognitoUser, currentPassword, newPassword);

    yield call(resolve);

    yield call(toast.success, `Password successfully updated.`);

    yield put(signOutAction());
  } catch (error) {
    const { message } = error as Error;
    yield call(reject, new SubmissionError({ _error: message }));
  }
}

function* oauthSignIn(): Generator {
  try {
    yield put(clearErrors());
    const customProvider = (yield select(({ currentUser: { authProvider } }: AppState) => authProvider)) as string | '';

    if (!customProvider || customProvider.length < 1) {
      const message = 'SSO provider cannot be blank';
      yield put(setErrors({ provider: message }));
      throw new Error(message);
    } else {
      const { provider: validProvider } = (yield portalService.getValidSsoProvider(customProvider)) as {
        provider: string;
      };

      if (!validProvider) {
        yield put(setCurrentUserAuthProvider());
        navigate(AppRoutes.Sso);
      } else {
        yield Auth.federatedSignIn({ customProvider: validProvider });
      }
    }
  } catch (error) {
    const { message } = error as Error;
    console.error(`error:`, message); // eslint-disable-line no-console
  }
}

function* updateGroupConfig({ payload: { clientId } }: any): Generator {
  try {
    const { group = {} } = (yield select(({ currentUser }: AppState) => currentUser)) as CurrentUserState;
    const { configurators = [] } = group as Group;
    const config = configurators.find((configurator: Configurator) => configurator.clientId === clientId);
    if (config) {
      const updatedConfig = (yield call(fetchConfigVendor, config)) as Configurator;
      yield put(setGroupConfig(updatedConfig));
      yield put(getThemeFunc());
    }
  } catch (error) {
    const { message } = error as Error;
    yield call(toast.error, message);
  }
}

function* initiateImpersonation({ payload: { user: userToImpersonate } }: any): Generator {
  try {
    const { username: usernameToImpersonate } = userToImpersonate as GroupMember;
    const { user: { username = '' } = {} } = (yield select(
      ({ currentUser }: AppState) => currentUser,
    )) as CurrentUserState;

    const { session } = (yield portalService.initiateImpersonateUser(username, usernameToImpersonate)) as {
      session: string;
    };

    // Store session to be used in validation
    sessionStorage.setItem(SessionStorage.ImpersonationSession, session);

    yield put(openDialog({ dialog: Dialogs.Impersonation }));
  } catch (error) {
    let { errorMessage = defaultErrorMessage } = extractErrorProps(error);
    if (errorMessage === 'User does not exist in user pool') errorMessage = i18n.t(I18nKeys.UserMissingFromUserPool);

    yield put(impersonationFailed());
    yield call(toast.error, errorMessage);
  }
}

function* validateImpersonation({
  resolve,
  values: { [ImpersonationDialogFormFields.ValidationCode]: code },
}: any): Generator {
  try {
    const { impersonation: { user: { username: usernameToImpersonate = '', name = '' } = {} } = {} } = (yield select(
      ({ currentUser }: AppState) => currentUser,
    )) as CurrentUserState;

    const session = sessionStorage.getItem(SessionStorage.ImpersonationSession);

    if (!session) throw new Error('Impersonation session not found');

    const { authenticationResult: authResult } = (yield portalService.validateImpersonation(
      code,
      usernameToImpersonate,
      session,
    )) as {
      authenticationResult: {
        AccessToken: string;
        IdToken: string;
        RefreshToken: string;
      };
    };

    if (!authResult) throw new Error('Authentication failed. The validation code may be invalid.');

    yield Auth.signOut();
    clearUserLocalStorageItems();
    const cognitoUser = new AmazonCognitoUser({
      Username: usernameToImpersonate,
      Pool: new CognitoUserPool({
        UserPoolId: configuration.cognito.USER_POOL_ID || '',
        ClientId: configuration.cognito.APP_CLIENT_ID || '',
      }),
    });
    const cognitoSession = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: authResult.IdToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: authResult.AccessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: authResult.RefreshToken }),
    });
    cognitoUser.setSignInUserSession(cognitoSession);

    yield put(signedIn());
    yield take((action: any) =>
      [CurrentUserActionTypes.SIGNED_IN_SUCCESS, CurrentUserActionTypes.SIGNED_IN_FAILED].includes(action.type),
    );

    yield put(closeDialog());
    yield put(impersonationSuccess());
    yield navigate(AppRoutes.Portal);
    yield call(toast.success, `Impersonating ${name}`);

    yield call(resolve);
  } catch (error) {
    const { errorMessage = defaultErrorMessage } = extractErrorProps(error);
    yield put(closeDialog());
    yield put(impersonationFailed());
    yield call(toast.error, errorMessage);
  }
}

function* fetchUserPreferences(): Generator {
  try {
    let { preferences = {} } = (yield portalService.getUserPreferences()) as { preferences: any };

    if (!preferences[UserPreference.ClientDataPreferences]) {
      // Fetch previous client data preferences from local storage
      preferences = {
        ...preferences,
        [UserPreference.ClientDataPreferences]: JSON.parse(
          localStorage.getItem(LocalStorage.ClientDataPreferences) || '{}',
        ),
      };
    }

    if (preferences) yield put(setUserPreferences(preferences));
  } catch (error) {
    const { errorMessage = defaultErrorMessage } = extractErrorProps(error);
    yield call(toast.error, errorMessage);
  } finally {
    yield put(fetchUserPreferencesComplete());
  }
}

function* saveUserPreferences({ payload: { userPreference, preferences } }: any): Generator {
  try {
    const { preferences: existingPreferences = {} } = (yield select(
      ({ currentUser }: AppState) => currentUser,
    )) as CurrentUserState;

    if (!Object.values(UserPreference).includes(userPreference)) throw new Error('Invalid user preference key');

    if (preferences) {
      const newPreferences = { ...existingPreferences, [userPreference]: preferences };

      const { preferences: updatedPreferences = {} } = (yield portalService.saveUserPreferences(newPreferences)) as {
        preferences: any;
      };
      yield put(setUserPreferences(updatedPreferences));
    }
  } catch (error) {
    const { errorMessage = defaultErrorMessage } = extractErrorProps(error);
    yield call(toast.error, errorMessage);
  } finally {
    yield put(saveUserPreferencesComplete());
  }
}

function* recordUserEvent({ type, payload: { groupId, clientId, eventCategory, eventType } }: any): Generator {
  try {
    if (configuration.environment.STAGE === 'production') {
      switch (type) {
        case CurrentUserActionTypes.SIGNED_IN_SUCCESS:
          eventType = EventName.SignIn; // eslint-disable-line no-param-reassign
          break;
        case CurrentUserActionTypes.CHANGE_GROUP:
          eventType = EventName.Change; // eslint-disable-line no-param-reassign
          break;
        default:
          break;
      }

      yield portalService.addUserEvent(groupId, clientId, eventCategory, eventType);
    }
  } catch (error) {
    const { errorMessage = defaultErrorMessage } = extractErrorProps(error);
    yield call(toast.error, errorMessage);
  }
}

function* watchSignedIn(): Generator {
  yield takeLatest(CurrentUserActionTypes.SIGNED_IN, fetchCurrentUserData);
}

function* watchFetchCurrentUserData(): Generator {
  yield takeLatest(CurrentUserActionTypes.FETCH_CURRENT_USER_DATA, fetchCurrentUserData);
}

function* watchChangeGroup(): Generator {
  yield takeLatest(CurrentUserActionTypes.CHANGE_GROUP, changeGroup);
}

function* watchSubmitPasswordForm(): Generator {
  yield takeLatest(`${Forms.Password}_SUBMIT`, submitPasswordForm);
}

function* watchSubmitProfileForm(): Generator {
  yield takeLatest(`${Forms.Profile}_SUBMIT`, submitProfileForm);
}

function* watchSubmitPreferencesForm(): Generator {
  yield takeLatest(`${Forms.Preferences}_SUBMIT`, submitPreferencesForm);
}

function* watchSubmitForgotPasswordForm(): Generator {
  yield takeLatest(`${Forms.ForgotPassword}_SUBMIT`, sendForgotPasswordCode);
}

function* watchSubmitSignOutForm(): Generator {
  yield takeLatest(CurrentUserActionTypes.SIGN_OUT, signOut);
}

function* watchSubmitSignUpForm(): Generator {
  yield takeLatest(`${Forms.SignUp}_SUBMIT`, submitSignUp);
}

function* watchSubmitSignInForm(): Generator {
  yield takeLatest(`${Forms.SignIn}_SUBMIT`, submitSignIn);
}

function* watchOauthSignIn(): Generator {
  yield takeLatest(CurrentUserActionTypes.OAUTH_SIGN_IN, oauthSignIn);
}

function* watchUpdateGroupConfig(): Generator {
  yield takeLatest(CurrentUserActionTypes.UPDATE_GROUP_CONFIG, updateGroupConfig);
}

function* watchInitiateImpersonation(): Generator {
  yield takeLatest(CurrentUserActionTypes.INITIATE_IMPERSONATION, initiateImpersonation);
}

function* watchValidateImpersonation(): Generator {
  yield takeLatest(`${Forms.Impersonation}_SUBMIT`, validateImpersonation);
}

function* watchFetchUserPreferences(): Generator {
  yield takeLatest(CurrentUserActionTypes.FETCH_USER_PREFERENCES, fetchUserPreferences);
}

function* watchSaveUserPreferences(): Generator {
  yield takeLatest(CurrentUserActionTypes.SAVE_USER_PREFERENCES, saveUserPreferences);
}

function* watchRecordUserEvent(): Generator {
  yield takeLatest(CurrentUserActionTypes.SIGNED_IN_SUCCESS, recordUserEvent);
  yield takeLatest(CurrentUserActionTypes.CHANGE_GROUP, recordUserEvent);
  yield takeLatest(userEventAction, recordUserEvent);
}

export function* userSaga(): Generator {
  yield all([
    watchSignedIn(),
    watchChangeGroup(),
    watchSubmitForgotPasswordForm(),
    watchSubmitPasswordForm(),
    watchSubmitProfileForm(),
    watchSubmitPreferencesForm(),
    watchSubmitSignInForm(),
    watchSubmitSignOutForm(),
    watchSubmitSignUpForm(),
    watchOauthSignIn(),
    watchUpdateGroupConfig(),
    watchFetchCurrentUserData(),
    watchInitiateImpersonation(),
    watchValidateImpersonation(),
    watchFetchUserPreferences(),
    watchSaveUserPreferences(),
    watchRecordUserEvent(),
  ]);
}
